import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../Settings/SettingsProvider';
import { Filter, useSaveSettings } from '@ais-ui/websockets';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import React, { useMemo } from 'react';
import { PiStar, PiStarFill } from 'react-icons/pi';
import { isFilterAppliedOnStartUp } from './isFilterAppliedOnStartUp';

interface ApplyOnStartupButtonProps {
  filter: Filter;
}

export const ApplyOnStartupButton = ({ filter }: ApplyOnStartupButtonProps) => {
  const { messages } = useLocalization();
  const { settings, dataUpdatedAt } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { toast } = useToast();
  const filterId = filter.uuid;
  const isAppliedOnStartup = useMemo(() => {
    return isFilterAppliedOnStartUp(settings, filterId);
  }, [settings, dataUpdatedAt, filterId]);

  const onApplyOnStartup = () => {
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        favorite_filters: {
          enabled: false,
          filters: [
            ...(settings['ribbonPlugin.settings']?.favorite_filters?.filters ||
              []),
            filterId,
          ],
        },
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-start-up-filters-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsFavoriteFiltersSuccessUpdateMessage,
        });
      },
      onError: () => {
        toast({
          className: 'update-start-up-filters-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsFavoriteFiltersFailedUpdateMessage,
          variant: 'destructive',
        });
      },
    });
  };

  const onRemoveOnStartup = () => {
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        favorite_filters: {
          enabled: false,
          filters:
            settings[
              'ribbonPlugin.settings'
            ]?.favorite_filters?.filters?.filter(
              (id: string) => id !== filterId
            ) || [],
        },
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-start-up-filters-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsFavoriteFiltersSuccessHideMessage,
        });
      },
      onError: () => {
        toast({
          className: 'update-start-up-filters-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsFavoriteFiltersFailedHideMessage,
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="smIcon"
          variant="ghost"
          onClick={isAppliedOnStartup ? onRemoveOnStartup : onApplyOnStartup}
        >
          {isAppliedOnStartup ? (
            <PiStarFill className="h-4 w-4 text-yellow-400" />
          ) : (
            <PiStar className="h-4 w-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isAppliedOnStartup
          ? 'No longer apply filter on startup'
          : 'Apply filter on startup'}
      </TooltipContent>
    </Tooltip>
  );
};
